import type {
  AxonCheckView,
  AxonExpectationView,
  AxonOwner,
  AxonSubsystemView,
  PlatformHealthAxonOverviewResponse,
} from './types';
import type { StoplightValue } from './Stoplight';

export interface StoplightResolution {
  value: StoplightValue;
  reason: string;
}

const RED: StoplightResolution = { value: 'red', reason: 'Health cannot be safely attested.' };

function resolution(value: StoplightValue, reason: string): StoplightResolution {
  return { value, reason };
}

function knownOwner(owner: AxonOwner | null | undefined): boolean {
  return Boolean(owner?.id && owner.displayName && owner.displayName.toLowerCase() !== 'unknown');
}

function canonicalRed(state: string): boolean {
  return ['blocked', 'unhealthy', 'unknown'].includes(state);
}

function nonActionableFindings(
  activeFindingCount: number,
  actionableFindingCount: number
): StoplightResolution | null {
  return activeFindingCount > actionableFindingCount
    ? resolution('amber', 'Active findings are waived or quarantined and remain non-actionable.')
    : null;
}

function worst(children: readonly StoplightResolution[]): StoplightResolution {
  const applicable = children.filter((child) => child.value !== 'neutral');
  if (applicable.length === 0) return resolution('neutral', 'No applicable children are declared.');
  return (
    applicable.find((child) => child.value === 'red') ??
    applicable.find((child) => child.value === 'amber') ??
    applicable.find((child) => child.value === 'green') ??
    RED
  );
}

/**
 * Presentation-only, fail-closed reduction. Canonical AXON state stays untouched;
 * this translates the evidence supplied by the overview into an ephemeral RAG light.
 */
export function resolveExpectation(
  expectation: AxonExpectationView,
  hasActionableFinding = false
): StoplightResolution {
  const { status } = expectation;
  if (hasActionableFinding) {
    return resolution('red', 'The owning check has an active or blocking finding.');
  }
  if (expectation.problemIds.length > 0) {
    return resolution('red', 'Reducer reported unresolved expectation problems.');
  }
  if (canonicalRed(expectation.healthState)) {
    return resolution('red', `Canonical expectation state is ${expectation.healthState}.`);
  }
  if (
    ['missing', 'never_seen', 'error', 'conflict', 'unknown', 'stale'].includes(
      expectation.conclusion
    )
  ) {
    return resolution(
      'red',
      `Expectation conclusion is ${expectation.conclusion.replace(/_/g, ' ')}.`
    );
  }
  if (status.applicability === 'not_applicable' || expectation.conclusion === 'not_applicable') {
    return resolution('neutral', 'Expectation is explicitly not applicable.');
  }
  if (status.applicability !== 'applicable') {
    return resolution('red', 'Expectation applicability is not known.');
  }
  if (
    status.completeness === 'missing' ||
    status.completeness === 'never_seen' ||
    status.completeness === 'unknown'
  ) {
    return resolution('red', `Evidence completeness is ${status.completeness.replace(/_/g, ' ')}.`);
  }
  if (status.freshness === 'stale' || status.freshness === 'unknown') {
    return resolution('red', `Evidence freshness is ${status.freshness}.`);
  }
  if (
    !expectation.currentAttestation ||
    expectation.evidence.length === 0 ||
    expectation.evidence.some((item) => item.missing)
  ) {
    return resolution('red', 'Current attestation or required evidence is missing.');
  }
  if (
    expectation.healthState === 'degraded' ||
    expectation.healthState === 'waived' ||
    ['warning', 'skipped', 'late', 'waived'].includes(expectation.conclusion) ||
    status.freshness === 'late' ||
    status.completeness === 'partial' ||
    ['waived', 'quarantined'].includes(status.policy) ||
    status.outcome === 'warn' ||
    status.outcome === 'skipped'
  ) {
    return resolution(
      'amber',
      'Evidence is usable but degraded, waived, late, partial, or recovering.'
    );
  }
  if (
    expectation.healthState === 'healthy' &&
    expectation.conclusion === 'pass' &&
    status.outcome === 'pass' &&
    status.freshness === 'fresh' &&
    status.completeness === 'complete' &&
    status.policy === 'active'
  ) {
    return resolution('green', 'Complete, fresh, applicable active PASS evidence is current.');
  }
  return RED;
}

export function resolveCheck(check: AxonCheckView): StoplightResolution {
  if (!knownOwner(check.owner))
    return resolution('red', 'The registered check has no known owner.');
  if (check.actionableFindingCount > 0 || check.blockingFindingCount > 0) {
    return resolution('red', 'The check has active or blocking findings.');
  }
  if (canonicalRed(check.state))
    return resolution('red', `Canonical check state is ${check.state}.`);
  if (
    check.registration === 'enabled' &&
    (check.coverage === 'missing_expectation' || check.expectations.length === 0)
  ) {
    return resolution('red', 'Enabled check has no declared expectation.');
  }
  if (check.problemIds.length > 0)
    return resolution('red', 'Reducer reported unresolved check problems.');
  const child = worst(
    check.expectations.map((expectation) =>
      resolveExpectation(
        expectation,
        check.actionableFindingCount > 0 || check.blockingFindingCount > 0
      )
    )
  );
  if (child.value === 'red') return child;
  const activeFindingResolution = nonActionableFindings(
    check.activeFindingCount,
    check.actionableFindingCount
  );
  if (activeFindingResolution) return activeFindingResolution;
  if (check.registration !== 'enabled') {
    return resolution('neutral', `Check is ${check.registration.replace(/_/g, ' ')}.`);
  }
  if (check.state === 'degraded' || check.state === 'waived') {
    return resolution('amber', `Canonical check state is ${check.state}.`);
  }
  return child.value === 'green' && check.state !== 'healthy' ? RED : child;
}

export function resolveSubsystem(subsystem: AxonSubsystemView): StoplightResolution {
  if (!knownOwner(subsystem.owner)) return resolution('red', 'The subsystem has no known owner.');
  if (subsystem.actionableFindingCount > 0 || subsystem.blockingFindingCount > 0) {
    return resolution('red', 'The subsystem has active or blocking findings.');
  }
  if (canonicalRed(subsystem.state))
    return resolution('red', `Canonical subsystem state is ${subsystem.state}.`);
  if (subsystem.checks.length === 0)
    return resolution('red', 'Registered subsystem has no checks.');
  if (!subsystem.selfHealth) return resolution('red', 'Required subsystem self-health is missing.');
  if (subsystem.blockingBlindSpotCount > 0)
    return resolution('red', 'Subsystem has unresolved evidence blind spots.');
  const child = worst(subsystem.checks.map(resolveCheck));
  if (child.value === 'red') return child;
  const activeFindingResolution = nonActionableFindings(
    subsystem.activeFindingCount,
    subsystem.actionableFindingCount
  );
  if (activeFindingResolution) return activeFindingResolution;
  if (subsystem.state === 'degraded')
    return resolution('amber', 'Canonical subsystem state is degraded.');
  return child.value === 'green' && subsystem.state !== 'healthy' ? RED : child;
}

export function resolveSystem(payload: PlatformHealthAxonOverviewResponse): StoplightResolution {
  if (!payload.available) return resolution('red', 'AXON source is unavailable.');
  if (payload.partial || payload.gaps.length > 0)
    return resolution('red', 'AXON source is partial or has unavailable references.');
  if (!payload.system) return resolution('red', 'Canonical system attestation is missing.');
  if (
    payload.findings.actionable > 0 ||
    payload.findings.critical > 0 ||
    payload.system.blockingFindingIds.length > 0
  ) {
    return resolution('red', 'System has active, critical, or blocking findings.');
  }
  if (canonicalRed(payload.system.state))
    return resolution('red', `Canonical system state is ${payload.system.state}.`);
  if (!payload.watchdog.available || payload.watchdog.status !== 'pass' || payload.watchdog.stale) {
    return resolution(
      'red',
      'Independent AXON watchdog is unavailable, failing, unknown, or stale.'
    );
  }
  if (payload.subsystems.length === 0)
    return resolution('red', 'No registered subsystems are available to attest.');
  const children = worst(payload.subsystems.map(resolveSubsystem));
  if (children.value === 'red') return children;
  const activeFindingResolution = nonActionableFindings(
    payload.findings.active,
    payload.findings.actionable
  );
  if (activeFindingResolution) return activeFindingResolution;
  if (payload.system.state === 'degraded')
    return resolution('amber', 'Canonical system state is degraded.');
  if (children.value === 'neutral') return children;
  return children.value === 'green' && payload.system.state === 'healthy'
    ? children
    : children.value === 'amber'
      ? children
      : RED;
}
